import React, { useEffect, useMemo, useState } from 'react';
import Chip from '../../ui/Chip.jsx';
import Badge from '../../ui/Badge.jsx';
import { formatMinutesToHM, formatStatusLabel } from '../../ui/format';
import { fetchAssignment } from '../../api';

function fmtYmd(v) {
  if (!v) return '—';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s;
}

function fmtBool(v) {
  if (v == null) return '—';
  return v ? 'Yes' : 'No';
}

function normalizeProgress(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if ([0, 25, 50, 75, 100].includes(n)) return n;
  const rounded = Math.round(n / 25) * 25;
  return Math.max(0, Math.min(100, rounded));
}

function fmtAccepted(acceptedAt) {
  return acceptedAt ? 'Accepted' : 'Not accepted';
}

function fmtProgressLabel(v) {
  const p = normalizeProgress(v);
  if (p === 100) return 'Done';
  return `${p}%`;
}

function fmtLoggedMinutes(v) {
  const n = Number(v || 0);
  return formatMinutesToHM(Number.isFinite(n) ? n : 0);
}

function fmtPlannedMinutes(v) {
  const n = Number(v || 0);
  return formatMinutesToHM(Number.isFinite(n) ? n : 0);
}

export default function TaskDetailsModal({ open, onClose, assignment, onEdit, onDelete }) {
  const assignmentId = assignment?.id || assignment?.assignment_id || null;
  const [live, setLive] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!open) {
        setLive(null);
        setRefreshing(false);
        return;
      }
      if (!assignmentId) {
        setLive(null);
        setRefreshing(false);
        return;
      }
      setRefreshing(true);
      try {
        const data = await fetchAssignment(assignmentId);
        if (!cancelled && data) setLive(data);
      } catch (_) {
        // Keep the passed-in calendar object as fallback.
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, assignmentId]);

  const a = live || assignment || null;

  const clientDisplayName = useMemo(() => {
    const raw = String(a?.client_name ?? '').trim();
    if (!raw || raw === '—') return 'No client';
    return raw;
  }, [a]);

  const assignees = useMemo(() => {
    const list = Array.isArray(a?.assignees) ? a.assignees : [];
    return list
      .filter(x => x && x.user_id != null)
      .map(x => ({
        user_id: x.user_id,
        display_name: x.display_name || `#${x.user_id}`,
        accepted_at: x.accepted_at ?? null,
        progress: x.progress ?? null,
        logged_minutes: x.logged_minutes ?? null,
        planned_minutes: x.planned_minutes ?? null,
      }));
  }, [a]);

  const headerTitle = useMemo(() => {
    const proj = a?.project_name || (a?.project_id ? `Project #${a.project_id}` : 'Task');
    const job = a?.job_name || '—';
    const task = a?.task_name ? ` › ${a.task_name}` : '';
    return `${proj} — ${job}${task}`;
  }, [a]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[6px]" onClick={() => onClose && onClose()} />

      <div className="absolute right-1/2 translate-x-1/2 top-16 w-[720px] max-w-[96vw] bg-white dark:bg-gray-900 rounded shadow-lg border border-gray-200 dark:border-gray-800 p-4 max-h-[82vh] overflow-auto">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">Task details</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
              {headerTitle}{refreshing ? ' • Refreshing…' : ''}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1 rounded text-sm bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!a?.id}
              title={a?.id ? 'Edit' : 'Edit not available'}
              onClick={() => onEdit && onEdit(live || a)}
            >
              Edit
            </button>
            {onDelete && (
              <button
                type="button"
                className="px-3 py-1 rounded text-sm border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-900/20"
                disabled={!assignmentId}
                title={assignmentId ? 'Delete task' : 'Delete not available'}
                onClick={() => onDelete(live || a)}
              >
                Delete task
              </button>
            )}
            <button onClick={() => onClose && onClose()} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Project</div>
              <div className="font-medium">{a?.project_name || (a?.project_id ? `#${a.project_id}` : '—')}</div>
              <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-400">
                <span className="text-gray-500 dark:text-gray-400">Client:</span>{' '}
                <span className="text-gray-800 dark:text-gray-200">{clientDisplayName}</span>
              </div>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Status</div>
              <div className="font-medium">{formatStatusLabel(a?.status) || '—'}</div>
            </div>
          </div>

          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Job</div>
            <div className="font-medium">{a?.job_name || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Task</div>
            <div className="font-medium">{a?.task_name || '—'}</div>
          </div>

          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Assignee(s)</div>
            {assignees.length === 0 ? (
              <div className="text-gray-700 dark:text-gray-300">—</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {assignees.map(p => (
                  <Chip key={p.user_id} label={p.display_name} />
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Planned hours</div>
            <div className="font-medium">{a?.planned_hours != null ? String(a.planned_hours) : '—'}</div>
          </div>

          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Hours mode</div>
            <div className="font-medium">{a?.split_mode || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Include weekends</div>
            <div className="font-medium">{fmtBool(a?.include_weekends)}</div>
          </div>

          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Start date</div>
            <div className="font-medium">{fmtYmd(a?.start_date)}</div>
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Due date</div>
            <div className="font-medium">{fmtYmd(a?.due_date)}</div>
          </div>

          <div className="col-span-2">
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Notes</div>
            <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{(a?.notes && String(a.notes).trim()) ? String(a.notes) : '—'}</div>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-200 dark:border-gray-800 pt-3">
          <div className="text-sm font-semibold">Assignees</div>
          <div className="text-[11px] text-gray-600 dark:text-gray-400">Read-only</div>

          <div className="mt-2 overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[11px] text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                  <th className="py-1 pr-3">Name</th>
                  <th className="py-1 pr-3">Accepted</th>
                  <th className="py-1 pr-3">Progress</th>
                  <th className="py-1 pr-3 text-right">Logged</th>
                  <th className="py-1 pr-0 text-right">Planned</th>
                </tr>
              </thead>
              <tbody>
                {assignees.length === 0 ? (
                  <tr>
                    <td className="py-2 text-gray-600 dark:text-gray-400" colSpan={5}>—</td>
                  </tr>
                ) : (
                  assignees.map(p => (
                    <tr key={`emp-${p.user_id}`} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-1 pr-3">{p.display_name}</td>
                      <td className="py-1 pr-3">{fmtAccepted(p.accepted_at)}</td>
                      <td className="py-1 pr-3">
                        <Badge
                          label={fmtProgressLabel(p.progress)}
                          variant={normalizeProgress(p.progress) === 100 ? 'success' : 'neutral'}
                        />
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums">{fmtLoggedMinutes(p.logged_minutes)}</td>
                      <td className="py-1 pr-0 text-right tabular-nums">{fmtPlannedMinutes(p.planned_minutes)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
